// Inbox's pieces (pages/ux-full components/inbox.js): one agent's request, and the notifications. Each
// draws from ready-to-show words (inbox-schemas.ts), so the office and a chat card draw them the same
// way. They draw no frame: the screen puts them in a section. Deciding and marking read are the
// screen's callbacks, drawn only when the screen passes them; a chat never decides (DEC-009: a tap in
// a card is an ordinary tool call), it opens Flowology.
import { useState } from 'react';
import type { AgentRequestProps, NoteListProps } from '../inbox-schemas.ts';
import type { ViewProps } from '../host.ts';
import { Action, Facts, Label } from '../primitives.tsx';

export interface Decision {
  decision: 'approve' | 'decline';
  /** Approve, and let the person's own AI tool act as them for trustHours (approvals.decide's trustConnection). */
  trust?: boolean;
  /** A decline's note, which the agent reads next time. */
  note?: string;
}

const DECIDED: Record<AgentRequestProps['state'], string> = {
  pending: 'Waiting for you',
  approved: 'Approved',
  declined: 'Declined',
  expired: 'Lapsed',
};

/** One request (approvals.list): who asked, why, exactly what will run, and the decision. */
export function AgentRequest({
  data: r,
  host,
  onDecide,
  busy = false,
}: ViewProps<AgentRequestProps> & {
  /** The office decides here (approvals.decide); without it (a chat) the card opens Flowology. */
  onDecide?: (d: Decision) => void;
  /** A decision is on its way: nothing can be pressed twice. */
  busy?: boolean;
}) {
  const chat = host.surface === 'chat';
  const [declining, setDeclining] = useState(false);
  const [note, setNote] = useState('');
  const waiting = r.state === 'pending';
  const rows = r.rows && (chat ? r.rows.slice(0, 4) : r.rows);
  const d = r.decided;
  const ran =
    d?.ran === 'done'
      ? r.asker.tool
        ? ' It ran as you.'
        : ' It ran as the agent, with you as the approver.'
      : d?.ran === 'running'
        ? ' It is running.'
        : d?.ran === 'failed'
          ? ` It did not run: ${d.failed ?? 'it was refused'}.`
          : '';
  return (
    <article className="flw-req" aria-label={r.title}>
      <p className="flw-req-state">
        {r.money && <Label>{r.money}</Label>}
        {waiting ? (
          <Label kind="wait">Waiting for you</Label>
        ) : (
          <Label kind={r.state === 'approved' ? 'now' : 'muted'}>{DECIDED[r.state]}</Label>
        )}
      </p>
      <h3 className="flw-title" title={r.action}>
        {r.title}
      </h3>
      <p className="flw-sub">
        {r.asker.name}, asked {r.asked}
      </p>
      {r.flagged && (
        <p className="flw-req-line">
          <Label kind="bad">Instructions found in customer text</Label>
        </p>
      )}
      {r.explanation && (
        <p className="flw-req-line">
          <span className="flw-muted">Why: </span>
          {r.explanation}
        </p>
      )}
      {rows ? (
        <>
          <p className="flw-sub flw-req-lead">Exactly what will run (it can’t be changed)</p>
          <Facts
            rows={rows.map((x) => [
              x.label,
              x.exact && x.exact !== x.text ? <span title={x.exact}>{x.text}</span> : x.text,
            ])}
          />
        </>
      ) : (
        <p className="flw-sub flw-req-lead">The details show only to people who could do this themselves.</p>
      )}
      {r.money && waiting && !chat && (
        <p className="flw-sub">Money: approving also needs the owner’s grant for {r.money.toLowerCase()}s.</p>
      )}
      {!waiting && (
        <p className={`flw-said${r.state === 'approved' ? '' : ' flw-said-off'}`} role="status">
          <b>
            {DECIDED[r.state]}
            {/* a time ends in "a.m.": its own dot ends the sentence */}
            {d?.at ? ` ${d.at.replace(/\.$/, '')}` : ''}.
          </b>
          {ran}
          {d?.note && <span className="flw-sub">Note to the agent: “{d.note}”</span>}
        </p>
      )}
      {waiting &&
        (chat || !onDecide ? (
          <div className="flw-acts">
            <Action primary={!chat} onClick={() => host.open({ kind: 'approvals' })}>
              Open in Flowology to decide
            </Action>
          </div>
        ) : r.cannotDecide ? (
          <p className="flw-waits">{r.cannotDecide}</p>
        ) : declining ? (
          <div className="flw-req-decline">
            <label className="flw-fld">
              <span>Note for the agent (it reads this next time)</span>
              <input
                className="flw-field"
                value={note}
                maxLength={500}
                onChange={(e) => setNote(e.target.value)}
              />
            </label>
            <div className="flw-acts">
              <button
                type="button"
                className="flw-btn flw-btn-pri"
                disabled={busy}
                onClick={() =>
                  onDecide({ decision: 'decline', ...(note.trim() ? { note: note.trim() } : {}) })
                }
              >
                {busy ? 'Declining…' : 'Decline'}
              </button>
              <button type="button" className="flw-link" disabled={busy} onClick={() => setDeclining(false)}>
                Back
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* the one primary: "act as me" where it's offered (the prototype's recommended way), else Approve */}
            <div className="flw-acts">
              {r.canTrust && (
                <button
                  type="button"
                  className="flw-btn flw-btn-pri"
                  disabled={busy}
                  onClick={() => onDecide({ decision: 'approve', trust: true })}
                >
                  Approve, and let it act as me for {r.trustHours} hours
                </button>
              )}
              <button
                type="button"
                className={r.canTrust ? 'flw-link' : 'flw-btn flw-btn-pri'}
                disabled={busy}
                onClick={() => onDecide({ decision: 'approve' })}
              >
                {busy ? 'Approving…' : 'Approve & run'}
              </button>
              <button type="button" className="flw-link" disabled={busy} onClick={() => setDeclining(true)}>
                Decline…
              </button>
            </div>
            {r.expires && <p className="flw-sub">It lapses {r.expires} if nobody decides.</p>}
          </>
        ))}
    </article>
  );
}

/** The notifications (notifications.list) by group, newest first; each opens where it's dealt with. */
export function NoteList({
  data,
  onOpen,
  onMarkRead,
  busy,
}: {
  data: NoteListProps;
  /** Opens a notification's place (and marks it read): the screen's. */
  onOpen?: (id: string) => void;
  /** Mark one read: the screen's (notifications.markRead). */
  onMarkRead?: (id: string) => void;
  /** The one being marked. */
  busy?: string | null;
}) {
  if (!data.groups.length)
    return (
      <p className="flw-empty" role="status">
        Nothing needs you right now. Jobs ready for review, payments online, tasks given to you and what a
        customer asks land here.
      </p>
    );
  return (
    <>
      {data.groups.map((g) => (
        <div key={g.key} className="flw-notes" role="group" aria-label={g.label}>
          <h3 className="flw-notes-head">
            {g.label} <span className="flw-cnt">{g.notes.length}</span>
          </h3>
          <ul className="flw-rows">
            {g.notes.map((n) => {
              const kind = n.read ? 'read' : n.urgent ? 'urgent' : 'new';
              return (
                <li key={n.id} className={`flw-row flw-note flw-note-${kind}`}>
                  <span className="flw-note-main">
                    {/* unread: a filled mark, red only when it's urgent; read: an outline */}
                    <svg className="flw-note-mark" viewBox="0 0 10 10" aria-hidden="true">
                      <circle cx="5" cy="5" r="4" />
                    </svg>
                    <span>
                      <span className="flw-sub">
                        {n.time}
                        {kind === 'read' ? '' : kind === 'urgent' ? ', urgent' : ', unread'}
                      </span>
                      {onOpen ? (
                        <button type="button" className="flw-open" onClick={() => onOpen(n.id)}>
                          {n.title}
                        </button>
                      ) : (
                        <b className="flw-open">{n.title}</b>
                      )}
                      {n.body && <span className="flw-sub">{n.body}</span>}
                    </span>
                  </span>
                  {!n.read && onMarkRead && (
                    <Action onClick={() => onMarkRead(n.id)} label={`Mark read: ${n.title}`}>
                      {busy === n.id ? 'Marking…' : 'Mark read'}
                    </Action>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </>
  );
}
